import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAppState } from "../../context/AppStateContext";
import { sortConstructionPhases } from "../../utils/constructionTimeline";
import { ConstructionGanttView } from "../../components/BeneficiaryDedicatedTimeline";
import { fetchBeneficiaryTimelineProgress } from "../../api/subcontractorPortalApi";
import type { PhasePortalPhotoBundle } from "../../utils/timelineGanttPortalPhotos";

/** Add `days` calendar days to a YYYY-MM-DD string and return formatted date. */
function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDate(d: string) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

export function WorkProgressTab() {
  const { id } = useParams<{ id: string }>();
  const { getById, constructionTimelinePhases, refreshApplicationsFromApi } = useAppState();
  const app = id ? getById(id) : undefined;

  useEffect(() => {
    void refreshApplicationsFromApi();
  }, [id, refreshApplicationsFromApi]);

  const sortedPhases = useMemo(
    () => sortConstructionPhases(constructionTimelinePhases),
    [constructionTimelinePhases],
  );

  const [phasePortalPhotos, setPhasePortalPhotos] = useState<Partial<Record<string, PhasePortalPhotoBundle>>>({});

  useEffect(() => {
    if (!app?.id) {
      setPhasePortalPhotos({});
      return;
    }
    let cancelled = false;
    void fetchBeneficiaryTimelineProgress(app.id)
      .then((rows) => {
        if (cancelled) return;
        const m: Partial<Record<string, PhasePortalPhotoBundle>> = {};
        for (const r of rows) {
          if (r.photo_data || r.photo_data_2 || r.photo_data_3) {
            m[r.timeline_phase_id] = {
              photo1: r.photo_data,
              photo2: r.photo_data_2,
              photo3: r.photo_data_3,
            };
          }
        }
        setPhasePortalPhotos(m);
      })
      .catch(() => {
        if (!cancelled) setPhasePortalPhotos({});
      });
    return () => {
      cancelled = true;
    };
  }, [app?.id]);

  if (!app) return null;

  const actualDays       = app.constructionTimelineActualDays ?? {};
  const actualDates      = app.constructionTimelineActualDates ?? {};
  const actualStartDates = app.constructionTimelineActualStartDates ?? {};
  const plannedStart = app.plannedStartDate ?? null;

  // Build phase calendar schedule with planned + actual start/end dates
  let cumulativeDays = 0;

  const phaseSchedule = plannedStart
    ? sortedPhases.map((p) => {
        const approx    = Math.max(0, Math.floor(Number(p.approxDays)) || 0);
        const plannedStartDate = addDays(plannedStart, cumulativeDays);
        cumulativeDays += approx;
        const plannedEndDate   = addDays(plannedStart, cumulativeDays);

        const rawActual = actualDays[p.id];
        const actual    = typeof rawActual === "number" && rawActual >= 0 ? rawActual : null;

        // Actual end = approval date stored from timeline approval
        const actualEndRaw  = actualDates[p.id] ?? null;
        const actualEndDate = actualEndRaw ? fmtDate(actualEndRaw) : null;

        // Actual start = date saved by subcontractor via set-phase-start (authoritative)
        const actualStartRaw  = actualStartDates[p.id] ?? null;
        const actualStartDate = actualStartRaw ? fmtDate(actualStartRaw) : null;

        return { phase: p, plannedStartDate, plannedEndDate, approx, actual, actualStartDate, actualEndDate };
      })
    : [];

  return (
    <>
      <p className="tab-intro">
        <strong>Construction work progress</strong> for this beneficiary — tracked against the master
        construction timeline. The Gantt chart below shows planned vs. actual progress for each phase.
      </p>

      {/* ── Planned start date banner ────────────────────────────────────── */}
      {plannedStart ? (
        <div className="wp-start-banner">
          <span className="wp-start-banner__icon">📅</span>
          <div>
            <span className="wp-start-banner__label">Project start date</span>
            <span className="wp-start-banner__date">{fmtDate(plannedStart)}</span>
          </div>
          <span className="wp-start-banner__note">
            Phase dates below are calculated from this anchor.
          </span>
        </div>
      ) : (
        <div className="wp-start-banner wp-start-banner--unset">
          <span className="wp-start-banner__icon">⚠️</span>
          <div>
            <span className="wp-start-banner__label">No planned start date set</span>
            <span className="wp-start-banner__note">
              Set a start date on the{" "}
              <Link to="/project-initiation" className="link">
                Project Initiation
              </Link>{" "}
              page to unlock calendar-based phase tracking.
            </span>
          </div>
        </div>
      )}

      {/* ── Phase schedule table (only when start date is set) ──────────── */}
      {plannedStart && phaseSchedule.length > 0 && (
        <div className="card card--elevated" style={{ marginBottom: "1.5rem" }}>
          <h2 className="card__title card__title--gov" style={{ marginBottom: "0.75rem" }}>
            📆 Phase Calendar Schedule
          </h2>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Phase</th>
                  <th className="geo-reports__num">Planned Days</th>
                  <th>Planned Start</th>
                  <th>Planned End</th>
                  <th>Actual Start</th>
                  <th>Actual End</th>
                  <th className="geo-reports__num">Actual Days</th>
                  <th>Variance</th>
                </tr>
              </thead>
              <tbody>
                {phaseSchedule.map(({ phase, plannedStartDate, plannedEndDate, approx, actual, actualStartDate, actualEndDate }, idx) => {
                  const variance = actual !== null ? actual - approx : null;
                  return (
                    <tr key={phase.id}>
                      <td className="text-muted">{idx + 1}</td>
                      <td style={{ fontWeight: 500 }}>{phase.title}</td>
                      <td className="geo-reports__num">{approx}</td>
                      <td style={{ fontSize: "0.8125rem" }}>{plannedStartDate}</td>
                      <td style={{ fontSize: "0.8125rem", color: "#64748b" }}>{plannedEndDate}</td>
                      <td style={{ fontSize: "0.8125rem", color: actualStartDate ? "#0f172a" : undefined }}>
                        {actualStartDate ?? <span className="text-muted">—</span>}
                      </td>
                      <td style={{ fontSize: "0.8125rem", color: actualEndDate ? "#0891b2" : undefined }}>
                        {actualEndDate ?? <span className="text-muted">—</span>}
                      </td>
                      <td className="geo-reports__num">
                        {actual !== null ? (
                          <span style={{ fontWeight: 600, color: "#0891b2" }}>{actual}</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {variance !== null ? (
                          <span style={{ fontWeight: 600, color: variance > 0 ? "#dc2626" : variance < 0 ? "#16a34a" : "#64748b" }}>
                            {variance > 0 ? `+${variance}` : variance} days
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Gantt chart ─────────────────────────────────────────────────── */}
      <div
        className="card card--elevated beneficiary-dedicated-hub__panel beneficiary-dedicated-hub__panel--gantt"
        style={{ marginBottom: "1.5rem" }}
      >
        <h2 className="card__title card__title--gov" style={{ marginBottom: "0.5rem" }}>
          📊 Construction Timeline — Gantt Chart
        </h2>
        <ConstructionGanttView phases={sortedPhases} actualDays={actualDays} phasePortalPhotos={phasePortalPhotos} />
      </div>
    </>
  );
}
